import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

// SEC-P3: email opt-out landing page. Reached from the footer of every
// transactional email (also targeted by RFC 8058 List-Unsubscribe-Post).
// We POST/GET the address to /api/unsubscribe which is idempotent.

const API = process.env.REACT_APP_BACKEND_URL;

export default function Unsubscribe() {
  const [sp] = useSearchParams();
  const [email, setEmail] = useState(sp.get("email") || "");
  const token = sp.get("t") || "";
  const [status, setStatus] = useState("idle"); // idle | busy | done | error
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!email || !email.includes("@")) {
      setErr("Please enter a valid email.");
      return;
    }
    if (!token) {
      // SEC-A4: refuse to POST an unsigned request — the backend would
      // 403 it anyway, but showing a clearer message avoids confusion.
      setErr("This link is missing its security token. Please click the "
             + "unsubscribe link inside a SkillConnect email.");
      setStatus("error");
      return;
    }
    setStatus("busy");
    setErr("");
    try {
      await axios.post(`${API}/api/unsubscribe`, {
        email: email.toLowerCase().trim(), t: token,
      });
      setStatus("done");
    } catch (e) {
      setStatus("error");
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail
             : "Couldn't process the request. Please try again.");
    }
  };

  // If the URL already carries ?email=… & ?t=…, auto-submit for the
  // classic one-click flow. Missing token = show manual form + reason.
  useEffect(() => {
    const q = sp.get("email");
    const t = sp.get("t");
    if (q && q.includes("@") && t) {
      (async () => {
        setStatus("busy");
        try {
          await axios.get(`${API}/api/unsubscribe`, {
            params: { email: q.toLowerCase().trim(), t },
          });
          setStatus("done");
        } catch (e) {
          setStatus("error");
          const detail = e?.response?.data?.detail;
          setErr(typeof detail === "string" ? detail
                 : "Couldn't process the request.");
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="unsubscribe-page">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
        <div className="font-bold tracking-tight text-slate-900 text-xl mb-6">
          SKILL<span className="text-signal">·</span>CONNECT
        </div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-2">Unsubscribe</h1>
        <p className="text-sm text-slate-600 mb-6">
          Stop non-essential emails (product updates, recommendations,
          reminders). Account-critical messages such as password resets
          and offer letters will still be sent.
        </p>

        {status === "done" ? (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="unsubscribe-success">
            You&apos;ve been unsubscribed. <strong>{email}</strong> will no longer receive marketing emails from SkillConnect.
          </div>
        ) : (
          <>
            <label className="block text-xs font-medium text-slate-700 mb-1">Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input w-full mb-3"
              data-testid="unsubscribe-email-input"
            />
            {err && <p className="text-sm text-red-600 mb-3" data-testid="unsubscribe-error">{err}</p>}
            <button
              type="button"
              className="btn-primary w-full"
              onClick={submit}
              disabled={status === "busy"}
              data-testid="unsubscribe-submit-btn"
            >
              {status === "busy" ? "Unsubscribing…" : "Unsubscribe me"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
